import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  GlobeAltIcon,
  LinkIcon,
  EyeIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  SignalIcon,
  StopIcon,
  PlayIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { catalogoDigitalApi, type CatalogoDigitalConfig, type CatalogoDigitalStats } from '../api/catalogoDigital';
import { organizacaoApi, type Organizacao } from '../api/organizacao';
import { pesquisasService } from '../api/pesquisas';
import { FiltrosProduto, FiltroToggle } from '../components/ui/FiltrosProduto';
import { btnPrimario, inputIris } from '../components/ui/iris';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';
import type { PesquisaEmpresa, PesquisaAlmox } from '../types/pesquisas';

// ── Componentes auxiliares ────────────────────────────────────────────────────

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }> = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className={`w-full ${inputIris}`} />
  </div>
);

// ── Página ────────────────────────────────────────────────────────────────────

const CatalogoDigitalPage: React.FC = () => {
  const [config, setConfig] = useState<CatalogoDigitalConfig | null>(null);
  const [stats, setStats] = useState<CatalogoDigitalStats | null>(null);
  const [org, setOrg] = useState<Organizacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const confirm = useConfirmDialog();

  const [empresas, setEmpresas] = useState<PesquisaEmpresa[]>([]);
  const [almoxarifados, setAlmoxarifados] = useState<PesquisaAlmox[]>([]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [cfg, st, o, emp, alm] = await Promise.all([
        catalogoDigitalApi.obterConfig(),
        catalogoDigitalApi.obterStats(),
        organizacaoApi.obter().catch(() => null),
        pesquisasService.getEmpresas(),
        pesquisasService.getAlmoxarifados(),
      ]);
      setConfig(cfg);
      setStats(st);
      setOrg(o);
      setEmpresas(emp);
      setAlmoxarifados(alm);
    } catch { /* */ }
    finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    if (!config) return;
    setSalvando(true);
    setMensagem('');
    try {
      await catalogoDigitalApi.salvarConfig(config);
      setMensagem('Configuração salva!');
      await carregar();
      setTimeout(() => setMensagem(''), 3000);
    } catch { setMensagem('Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  // O endpoint /sincronizar agora roda o ciclo de sync inline (espera de fato
  // o Firebird + imagens). Frontend só precisa exibir spinner enquanto a
  // promise resolve — sem setTimeout artificial.
  const sincronizar = async () => {
    setSincronizando(true);
    setMensagem('Sincronizando catálogo... (pode levar até 1 minuto)');
    try {
      await catalogoDigitalApi.forcarSincronizacao();
      await carregar();
      setMensagem('Sincronização concluída!');
      setTimeout(() => setMensagem(''), 3000);
    } catch { setMensagem('Erro ao sincronizar'); }
    finally { setSincronizando(false); }
  };

  const ativar = async () => {
    if (!config) return;
    const novaConfig = { ...config, catalogoAtivo: true, syncAtivo: true };
    setConfig(novaConfig);
    setSincronizando(true);
    setMensagem('Ativando e sincronizando catálogo... (pode levar até 1 minuto)');
    try {
      await catalogoDigitalApi.salvarConfig(novaConfig);
      await catalogoDigitalApi.forcarSincronizacao();
      await carregar();
      setMensagem('Catálogo ativado!');
      setTimeout(() => setMensagem(''), 3000);
    } catch { setMensagem('Erro ao ativar catálogo'); }
    finally { setSincronizando(false); }
  };

  const parar = async () => {
    if (!config) return;
    const ok = await confirm({
      mensagem: 'Desativar o catálogo online? O link ficará indisponível.',
      destrutivo: true,
      textoConfirmar: 'Desativar',
    });
    if (!ok) return;
    const novaConfig = { ...config, catalogoAtivo: false, syncAtivo: false };
    setConfig(novaConfig);
    await catalogoDigitalApi.salvarConfig(novaConfig);
    setMensagem('Catálogo desativado.');
    await carregar();
    setTimeout(() => setMensagem(''), 3000);
  };

  const copiarLink = () => {
    if (!stats?.linkPublico) return;
    navigator.clipboard.writeText(stats.linkPublico);
    setLinkCopiado(true);
    setTimeout(() => setLinkCopiado(false), 2000);
  };

  if (carregando) {
    return <div className="p-6 flex items-center justify-center"><ArrowPathIcon className="w-5 h-5 animate-spin text-tx-3 mr-2" /><span className="text-tx-3">Carregando...</span></div>;
  }
  if (!config) return null;

  const online = config.catalogoAtivo && (stats?.totalCache ?? 0) > 0;
  const orgPreenchida = !!(org?.nomeFantasia || org?.razaoSocial);

  return (
    <div className="mx-auto max-w-[980px] space-y-4 md:space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <GlobeAltIcon className="w-7 h-7 text-accent-text" />
        <div className="flex-1">
          <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">Catálogo Digital Online</h1>
          <p className="text-xs text-tx-2">Configure e publique seu catálogo acessível por link externo</p>
        </div>
      </div>

      {/* Organização — aviso se não preenchida */}
      {!orgPreenchida && (
        <div className="flex items-center gap-3 px-4 py-3 bg-warning-soft border border-warning-border rounded-iris-lg">
          <BuildingOfficeIcon className="w-5 h-5 text-warning flex-shrink-0" />
          <div className="flex-1 text-sm text-warning">
            Configure os dados da sua empresa em <Link to="/organizacao" className="font-semibold underline">Configurações &gt; Organização</Link> para que apareçam no catálogo.
          </div>
        </div>
      )}

      {/* Organização resumida */}
      {orgPreenchida && (
        <div className="flex items-center gap-3 px-4 py-3 bg-surface border border-line rounded-iris-lg shadow-sm">
          {org?.logoBase64 ? (
            <img src={`data:image/png;base64,${org.logoBase64}`} alt="Logo" className="h-8 w-auto rounded" />
          ) : (
            <BuildingOfficeIcon className="w-5 h-5 text-tx-3" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-tx truncate">{org?.nomeFantasia || org?.razaoSocial}</p>
            <p className="text-xs text-tx-3 truncate">{org?.telefone} · {org?.email}</p>
          </div>
          <Link to="/organizacao" className="text-xs text-accent-text hover:underline">Editar</Link>
        </div>
      )}

      {/* Status Card */}
      <div className="bg-surface border border-line rounded-iris-lg shadow-sm p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${online ? 'bg-ok animate-pulse' : 'bg-[var(--text-3)]'}`} />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-tx">{online ? 'Online' : 'Offline'}</h2>
              <p className="text-xs text-tx-3">
                {stats?.totalCache ?? 0} produtos · {stats?.comImagem ?? 0} com imagem
                {stats?.ultimaSincronizacao && <> · Sync: {new Date(stats.ultimaSincronizacao).toLocaleString('pt-BR')}</>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {online ? (
              <>
                <button onClick={copiarLink}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-iris bg-accent hover:opacity-90 text-white transition-opacity shadow-sm">
                  <LinkIcon className="w-3.5 h-3.5" />
                  {linkCopiado ? 'Copiado!' : 'Copiar Link'}
                </button>
                <a href={stats?.linkPublico ?? '/catalogo'} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-iris bg-surface-2 border border-line text-tx hover:bg-surface transition-colors">
                  <EyeIcon className="w-3.5 h-3.5" /> Visualizar
                </a>
                <button onClick={sincronizar} disabled={sincronizando}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-iris bg-warning-soft text-warning hover:opacity-90 transition-opacity disabled:opacity-50">
                  {sincronizando ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <SignalIcon className="w-3.5 h-3.5" />}
                  Sincronizar
                </button>
                <button onClick={parar}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-iris bg-danger-soft text-danger hover:opacity-90 transition-opacity">
                  <StopIcon className="w-3.5 h-3.5" /> Parar
                </button>
              </>
            ) : (
              <>
                <button onClick={ativar}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-iris bg-accent hover:opacity-90 text-white transition-opacity shadow-sm">
                  <PlayIcon className="w-4 h-4" /> Ativar catálogo
                </button>
                <button onClick={sincronizar} disabled={sincronizando}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-iris bg-warning-soft text-warning hover:opacity-90 transition-opacity disabled:opacity-50">
                  {sincronizando ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <SignalIcon className="w-3.5 h-3.5" />}
                  Sincronizar
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mensagem */}
      {mensagem && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-iris text-sm ${
          mensagem.includes('Erro') ? 'bg-danger-soft text-danger' : 'bg-ok-soft text-ok'
        }`}>
          <CheckCircleIcon className="w-4 h-4" /> {mensagem}
        </div>
      )}

      {/* Configuração */}
      <div className="bg-surface border border-line rounded-iris-lg shadow-sm p-4 md:p-5 space-y-5">
        <div className="flex items-center gap-2">
          <Cog6ToothIcon className="w-4 h-4 text-tx-3" />
          <h2 className="text-sm font-semibold text-tx-2">Configuração</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Nome do catálogo" value={config.nomeCatalogo} onChange={v => setConfig({...config, nomeCatalogo: v})} />
          <div>
            <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Agrupar produtos por</label>
            <select value={config.agruparPor} onChange={e => setConfig({...config, agruparPor: e.target.value})}
              className={`w-full ${inputIris}`}>
              <option value="NAO AGRUPAR">Sem agrupamento</option>
              <option value="DESCRICAO MARCA">Por Marca</option>
              <option value="NOME GRUPO">Por Grupo</option>
              <option value="NOME SUBGRUPO">Por Subgrupo</option>
            </select>
          </div>
          <Field label="Dias para novidade" value={String(config.diasNovidade ?? 30)} onChange={v => setConfig({...config, diasNovidade: Math.max(1, Number(v) || 30)})} type="number" />
        </div>

        {/* Origem dos dados */}
        <div>
          <h3 className="text-xs font-semibold text-tx-3 uppercase tracking-wide mb-2">Origem dos dados (ERP)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Empresa</label>
              <select value={config.empresaCodigo ?? ''} onChange={e => setConfig({...config, empresaCodigo: e.target.value})}
                className={`w-full ${inputIris}`}>
                <option value="">-- Selecione --</option>
                {empresas.map(e => <option key={e.codigo} value={e.codigo}>{e.nomeFantasia || e.razaosocial}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Almoxarifado</label>
              <select value={config.almoxarifadoCodigo ?? ''} onChange={e => setConfig({...config, almoxarifadoCodigo: e.target.value})}
                className={`w-full ${inputIris}`}>
                <option value="">-- Todos --</option>
                {almoxarifados.map(a => <option key={a.codigoAlmox} value={a.codigoAlmox}>{a.descicaoAlmox}</option>)}
              </select>
            </div>
            <Field label="Marcas (códigos, vírgula)" value={config.marcasFiltro ?? ''} onChange={v => setConfig({...config, marcasFiltro: v})} placeholder="Vazio = todas" />
            <Field label="Grupos (códigos, vírgula)" value={config.gruposFiltro ?? ''} onChange={v => setConfig({...config, gruposFiltro: v})} placeholder="Vazio = todos" />
          </div>
        </div>

        {/* Sincronização */}
        <div>
          <h3 className="text-xs font-semibold text-tx-3 uppercase tracking-wide mb-2">Sincronização</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Intervalo automático (minutos)</label>
              <input
                type="number"
                min={0}
                value={String(config.intervaloSyncMinutos ?? 0)}
                onChange={e => {
                  const n = Number(e.target.value);
                  // 0 = desliga modo intervalo (use horário agendado ou sync manual).
                  // 1-4 vira 5 (mínimo prático pra não martelar o ERP).
                  const v = !isFinite(n) || n < 0 ? 0 : (n === 0 ? 0 : Math.max(5, Math.floor(n)));
                  setConfig({ ...config, intervaloSyncMinutos: v });
                }}
                className={`w-full ${inputIris}`}
              />
              <p className="text-[10px] text-tx-3 mt-0.5">0 = desligado. Se &gt; 0, sincroniza a cada X minutos. Mínimo 5 quando ligado.</p>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Horário agendado (diário)</label>
              <input type="time" value={config.horarioSyncAgendado ?? ''} onChange={e => setConfig({...config, horarioSyncAgendado: e.target.value || undefined})}
                className={`w-full ${inputIris}`} />
              <p className="text-[10px] text-tx-3 mt-0.5">Se preenchido, tem prioridade — sincroniza só nesse horário e ignora o intervalo.</p>
            </div>
          </div>
          {(config.intervaloSyncMinutos ?? 0) === 0 && !config.horarioSyncAgendado && (
            <p className="text-[11px] text-warning mt-2">
              ⚠ Com intervalo em 0 e sem horário agendado, a sincronização só roda manualmente pelo botão "Sincronizar agora".
            </p>
          )}
          <FiltrosProduto
            className="mt-3"
            value={config}
            onChange={patch => setConfig({ ...config, ...patch })}
            filters={['apenasComEstoque', 'apenasAtivo', 'apenasDisponivelVenda', 'apenasEnviaMobile', 'exibirEmTransito']}
          >
            {/* toggles de EXIBIÇÃO (não são filtro de produto) — mesma linha */}
            <FiltroToggle label="Exibir nome da empresa" checked={config.exibirNomeEmpresa ?? true} onChange={v => setConfig({ ...config, exibirNomeEmpresa: v })} />
            <FiltroToggle label="Exibir WhatsApp" checked={config.exibirWhatsapp ?? true} onChange={v => setConfig({ ...config, exibirWhatsapp: v })} />
          </FiltrosProduto>
        </div>

        {/* Salvar */}
        <div className="flex gap-2 pt-2 border-t border-line">
          <button onClick={salvar} disabled={salvando} className={btnPrimario}>
            {salvando ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
            {salvando ? 'Salvando...' : 'Salvar configuração'}
          </button>
        </div>
      </div>

    </div>
  );
};

export default CatalogoDigitalPage;
